import {HardwareGroupWithContent, HardwareItem, HwOpening} from "../../../../api/Hardware";
import {EditHardwareGroupWithContent, EditHardwareItem} from "./HwEditingContextProvider";
import {
    Alert,
    DialogContent,
    DialogTitle, FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {useEffect, useRef, useState} from "react";
import {range, sum} from "nate-react-api-helpers";
import {universalParser} from "../../../../misc/scroller/Table";
import {ImperialDistance} from "../../../../misc/ImperialDistance";
import {getHwOpeningType} from "./PendingHardware";
import {UnitSystem} from "../../../../api/Projects";
import {useProjectUnitSystem} from "../ProjectName";
import {MetricDistance} from "../../../../misc/MetricDistance";
import {calcDefaultHingeProduct, getHingeLines} from "./hingeProducts";

export function isMultiHinge(input: HardwareGroupWithContent|EditHardwareGroupWithContent) {
    const hinges = getHingeLines(input.hardware || []);
    return hinges.length > 1;
}

type PromptInfo = {
    hardware: HardwareGroupWithContent|EditHardwareGroupWithContent;
    onDone: (input: HardwareGroupWithContent|EditHardwareGroupWithContent) => void;
    onCancel: () => void;
}

export function useMultiHingeConfig() {

    const [info, setInfo] = useState<PromptInfo|null>(null);
    const [lastSucessfulPrompt, setLastSuccessfulPrompt] = useState<HardwareGroupWithContent|EditHardwareGroupWithContent>();
    const lastSuccessfulPromptRef = useRef(lastSucessfulPrompt);
    lastSuccessfulPromptRef.current = lastSucessfulPrompt;

    useEffect(() => {
        console.log("useMultiHingeConfig: init")
        return () => {
            console.log("useMultiHingeConfig: unmount")
        }
    }, [])

    const Element = info ?(
        <MultiHingeConfig
            hardware={info.hardware}
            onDone={info.onDone}
            onCancel={info.onCancel}
        />
    ) : null;

    return {
        prompt: async (init: HardwareGroupWithContent|EditHardwareGroupWithContent) => {
            return new Promise<HardwareGroupWithContent|EditHardwareGroupWithContent>((resolve, reject) => {
                setInfo({
                    hardware: init,
                    onDone: (input) => {
                        lastSuccessfulPromptRef.current = input;

                        resolve(input);
                        setInfo(null);
                        setLastSuccessfulPrompt(input);
                    },
                    onCancel: () => {
                        setInfo(null);
                        reject(new Error("Cancelled"));
                    },
                });
            })
        },
        shouldPrompt: (newValue: EditHardwareGroupWithContent, oldValue: EditHardwareGroupWithContent) => {
            if(!isMultiHinge(newValue)) {
                return false;
            }

            if(lastSuccessfulPromptRef.current) {
                if(linesHaveChanged(getHingeLines(newValue.hardware), getHingeLines(lastSuccessfulPromptRef.current.hardware))) {
                    return true;
                }
            } else if(linesHaveChanged(getHingeLines(newValue.hardware), getHingeLines(oldValue.hardware))) {
                return true
            }

            if(openingType(newValue.openings || []) !== openingType(oldValue.openings || [])) {
                return true
            }

            return false
        },
        has: (newValue: EditHardwareGroupWithContent) => {
            return isMultiHinge(newValue);
        },
        Element: Element,
    }
}

function openingType(list: Partial<HwOpening>[]) {
    if(list.length === 0) return "Single"
    return list[0].openingType;
}

function linesHaveChanged(aList: EditHardwareItem[], bList: EditHardwareItem[]) {
    const changed = aList.filter(a => {
        const b = bList.find(b => b.product === a.product);
        if(!b) return true;

        return !propsEq(a, b, "activeQty", "inactiveQty", "commonQty", "overrideHingeInActiveQty", "overrideHingeActiveQty")
    });

    if(changed.length > 0) return true;
    if(bList.length !== aList.length) return true;

    return false;
}

function propsEq<T>(a: T, b: T, ...props: (keyof T)[]) {
    for(const prop of props) {
        if(JSON.stringify(a[prop]) !== JSON.stringify(b[prop])) return false;
    }

    return true;
}

export function MultiHingeConfig(props: {
    hardware: HardwareGroupWithContent|EditHardwareGroupWithContent;
    onDone: (input: HardwareGroupWithContent|EditHardwareGroupWithContent) => void;
    onCancel: () => void;
}) {
    const hinges = getHingeLines(props.hardware.hardware || []);
    const calcDefaultProduct = calcDefaultHingeProduct(hinges);
    const [defaultProduct, setDefaultProduct] = useState(calcDefaultProduct || -1);

    const o = (props.hardware.openings || []).find(v => true);
    const isPair = !!o && getHwOpeningType(o.openingType, o.frameProfile === "COM") === "Pair";
    const isPairMul = isPair ? 2 : 1;

    const drwOpenings = (props.hardware.openings || []).filter(v => v.drwHardwareElevation != null).map(v => v.name);

    const hingeQty = getHingeQty(hinges, isPair) || 3 * isPairMul;

    const [qtyStr, setQtyStr] = useState((hingeQty).toString());
    const [qty, setQty] = useState(hingeQty);
    const perSideQty = isPair ? Math.ceil(qty / 2) : qty;

    const [active, setActive] = useState<number[]>([]);
    const [inActive, setInActive] = useState<number[]>([]);
    const hasInitRef = useRef(false);

    const columns = isPair ? 2 : 1;

    const hw = props.hardware;

    useEffect(() => {

        const updateDefaults = (list: number[], p: number) => {
            return list.map(l => {
                if(l === p) return -1;
                return l;
            })
        }

        if(!hasInitRef.current) {
            const active: number[] = [];
            const inActive: number[] = [];

            while(active.length < perSideQty) {
                active.push(-1);
            }

            while(inActive.length < perSideQty) {
                inActive.push(-1);
            }

            hw.hardware.forEach(h => {
                if (!h.hingePrepLocationConfig) return;

                h.hingePrepLocationConfig.active
                    .forEach(index => {
                        active[index] = h.product;
                    });

                h.hingePrepLocationConfig.inActive
                    .forEach(index => {
                        inActive[index] = h.product;
                    });
            });

            setActive(updateDefaults(active, defaultProduct));
            setInActive(updateDefaults(inActive, defaultProduct));

            hasInitRef.current = true;
        }

        setActive(old => {
            const newActive = old.slice(0, perSideQty);

            while (newActive.length < perSideQty) {
                newActive.push(-1);
            }

            return updateDefaults(newActive, defaultProduct);
        });

        setInActive(old => {
            const newInActive = old.slice(0, perSideQty)

            while (newInActive.length < perSideQty) {
                newInActive.push(-1);
            }

            return updateDefaults(newInActive, defaultProduct);
        });

    }, [perSideQty, hw, defaultProduct]);

    const sys = useProjectUnitSystem();
    if(sys === null) return null;

    const submit = () => props.onDone({
        ...props.hardware,
        hardware: props.hardware.hardware.map((h, i) => {
            if(!hinges.find(hg => hg.product === h.product)) return h;

            const activeLocations: number[] = [];
            const inActiveLocations: number[] = [];

            active.forEach((a, j) => {
                if(a === -1) {
                    a = defaultProduct;
                }

                if(a === h.product) {
                    activeLocations.push(j);
                }
            });

            inActive.forEach((a, j) => {
                if(a === -1) {
                    a = defaultProduct;
                }

                if(a === h.product) {
                    inActiveLocations.push(j);
                }
            });

            const op = props.hardware.openings.length > 0 ? props.hardware.openings[0] : undefined;
            let defaultActiveQty = op && op.nominalHeight ? getDefaultHingeQty(op.nominalHeight, sys) : 999999;
            let defaultInActiveQty = defaultActiveQty;

            let overrideActiveQty: number | null = null;
            if(activeLocations.length !== defaultActiveQty) {
                overrideActiveQty = activeLocations.length;
            }

            let overrideInActiveQty: number | null = null;
            if(inActiveLocations.length !== defaultInActiveQty) {
                overrideInActiveQty = inActiveLocations.length;
            }

            return {
                ...h,
                overrideHingeActiveQty: overrideActiveQty,
                overrideHingeInActiveQty: overrideInActiveQty,
                hingePrepLocationConfig: !hasLocationOverride(qty, isPair, activeLocations, inActiveLocations) ?
                        null : {
                            active: activeLocations,
                            inActive: inActiveLocations,
                        },
            }
        }),
    });

    return (
        <Dialog2 open onClose={() => props.onCancel()} onEnterPress={submit}>
            <DialogTitle>Multi-Hinge Config</DialogTitle>
            <DialogContent>
                {drwOpenings.length > 0 && <Alert severity="info" style={{marginBottom: 8}}>
                    Ignored for: {drwOpenings.join(", ")} (drw hardware elevation)
                </Alert>}
                <div style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    paddingTop: 8,
                }}>
                    <TextField
                        label="Qty"
                        variant="outlined"
                        value={qtyStr}
                        onChange={e => {
                            const val = e.target.value;
                            setQtyStr(val);

                            const num = parseInt(val);
                            setQty(num);
                        }}
                        onBlur={v => {
                            const val = v.target.value;
                            const num = parseInt(val);
                            setQtyStr(num.toString());
                            setQty(num);
                        }}
                        type="number"
                        style={{
                            width: 150
                        }}
                    />

                    <SelectProduct label="Default Product" value={defaultProduct} onChange={setDefaultProduct} products={hinges} />
                </div>

                <div style={{height: 20}} />

                <table><tbody>
                <tr>
                    <td>Active (top to bottom)</td>
                    <td style={{width: 100}}></td>
                    {isPair && <td>In-Active</td>}
                </tr>
                {range(Math.ceil(qty/columns)).map(i => (
                    <tr key={i}>
                        <td>
                            <SelectProduct
                                value={active[i] === -1 ? defaultProduct : active[i]}
                                onChange={v => {
                                    if(v === defaultProduct) {
                                        v = -1
                                    }

                                    setActive(old => {
                                        const newActive = old.slice();
                                        newActive[i] = v;
                                        return newActive;
                                    });
                                }}
                                products={hinges}
                            />
                        </td>
                        <td style={{
                            textAlign: "center"
                        }}>{(i+1).toString()}</td>
                        {isPair && <td>
                            <SelectProduct
                                value={inActive[i] === -1 ? defaultProduct : inActive[i]}
                                onChange={v => {
                                    if(v === defaultProduct) {
                                        v = -1
                                    }

                                    setInActive(old => {
                                        const newActive = old.slice();
                                        newActive[i] = v;
                                        return newActive;
                                    });
                                }}
                                products={hinges}
                            />
                        </td>}
                    </tr>
                ))}
                </tbody></table>

            </DialogContent>
            <SmoothActionBar
                onCancel={() => props.onCancel()}
                onSubmit={submit}
            />
        </Dialog2>
    )
}

export function getDefaultHingeQty(openingHeight: string, unitSys?: UnitSystem) {
    const height = unitSys === "metric" ? MetricDistance.parse(openingHeight).universalValue() : universalParser(openingHeight);
    if(height <= ImperialDistance.parse("60\"").universalValue()) return 2;
    if(height <= ImperialDistance.parse("90\"").universalValue()) return 3;
    if(height <= ImperialDistance.parse("120\"").universalValue()) return 4;
    if(height <= ImperialDistance.parse("11\'").universalValue()) return 5;
    throw new Error("hinge auto-qty: door is too tall (over 11')")
}

function hasLocationOverride(qty: number, isPair: boolean, actives: number[], inActives: number[]) {
    if(isPair) {
        if(actives.length === qty && inActives.length === qty) return false;
        return true
    }

    return actives.length !== qty;
}

function getHingeQty(input: Pick<HardwareItem, "overrideHingeActiveQty" | "overrideHingeInActiveQty" | "hingeActiveQtys" | "hingeInactiveQtys" | "activeQty" | "inactiveQty">[], isPair: boolean) {
    return input.reduce((acc, item) => {
        let qty = 0;

        if(item.overrideHingeActiveQty !== null && item.overrideHingeActiveQty !== undefined) {
            qty += item.overrideHingeActiveQty
        } else if(item.hingeActiveQtys) {
            qty += sum(item.hingeActiveQtys);
        } else {
            qty += item.activeQty;
        }

        if(isPair) {
            if (item.overrideHingeInActiveQty !== null && item.overrideHingeInActiveQty !== undefined) {
                qty += item.overrideHingeInActiveQty
            } else if (item.hingeInactiveQtys) {
                qty += sum(item.hingeInactiveQtys);
            } else {
                qty += item.inactiveQty;
            }
        }

        return acc + qty;
    }, 0)
}

function SelectProduct(props: {
    label?: string;
    value: number;
    onChange: (value: number) => void;
    products: HardwareItem[] | EditHardwareItem[];
}) {
    return (
        <FormControl>
            <InputLabel shrink>{props.label}</InputLabel>
            <Select label={props.label} variant="outlined" value={props.value?.toString() || -1} onChange={e => {
                props.onChange(parseInt(e.target.value as string))
            }}>
                <MenuItem value={-1}>Choose Default Product</MenuItem>
                {props.products.map(h => (
                    <MenuItem key={h.product} value={h.product.toString()}>
                        {h.productCode}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}